<template>
    <div>
        <top-navbar :breadcrumb="breadcrumb"></top-navbar>

        <div class="container-fluid mt--6">
            <div class="row">
                <div class="col">
                    <div class="card" v-show="loaded">

                        <div class="card-header border-0">
                            <div class="row">
                                <div class="col-6">
                                    <h3 class="mb-0 text-uppercase">{{ $t('reports.cohorts') }}</h3>
                                </div>
                                <div class="col-6 text-right">
                                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                                        {{ $t('common.clear-filters') }}
                                    </base-button>
                                </div>
                            </div>
                        </div>

                        <div class="pt-2">
                            
                            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

                            <el-table class="table-responsive table-flush"
                                    header-row-class-name="thead-light"
                                    :data="ctrl.rows"
                                    @sort-change="sortChange"
                                    @filter-change="filterChange"
                                    ref="table">

                                <el-table-column :label="$t('common.name')"
                                    sortable="custom" prop="name"
                                >
                                    <template v-slot="{row}">
                                        <h4 class="mb-0">
                                            {{ row.name }}
                                        </h4>
                                    </template>
                                </el-table-column>

                                <el-table-column :label="$t('common.status')"
                                    column-key="status"
                                    :filters="filtering.filteringValues(allStatusOptions)"
                                    :filtered-value="filters.status"
                                    filter-placement="bottom-end"
                                >
                                    <template v-slot="{row}">
                                        <badge :type="statusColor(row.status)">{{ statusLabel(row.status) }}</badge>
                                    </template>
                                </el-table-column>

                                <el-table-column :label="$t('common.owner')"
                                    column-key="owner"
                                    :filters="filtering.filteringValues(owners)"
                                    :filtered-value="filters.owner"
                                    filter-placement="bottom-end"
                                >
                                    <template v-slot="{row}">
                                        {{ owners[row.owner_id] }}
                                    </template>
                                </el-table-column>

                                <el-table-column align="right">
                                    <template v-slot="{row}">
                                        <div class="p-1">

                                            <el-dropdown trigger="click" @command="handleCommand(row.id, $event)">
                                                <base-button type="primary" class="dropdown-toggle btn-sm mr-0" @click.prevent.stop="">
                                                    <i class="tim-icons icon-line-chart mr-1"></i> {{ $t('common.reports') }}
                                                </base-button>
                                                <el-dropdown-menu slot="dropdown">
                                                    <el-dropdown-item command="learners">
                                                        {{ $t('reports.cohort-learners') }}
                                                    </el-dropdown-item>
                                                    <el-dropdown-item command="instructors">
                                                        {{ $t('reports.cohort-instructors') }}
                                                    </el-dropdown-item>
                                                    <el-dropdown-item command="degrees">
                                                        {{ $t('reports.cohort-degrees') }}
                                                    </el-dropdown-item>
                                                </el-dropdown-menu>
                                            </el-dropdown>
                                        </div>
                                    </template>
                                </el-table-column>

                            </el-table>

                            <table-pagination :paging="ctrl.paging"></table-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../classes/CrudFiltering'
    import CohortMapping from '../accounts/cohorts/CohortMapping'

    export default {

        data() {
            let mapping = new CohortMapping()
            let filtering = new CrudFiltering('class_reports')
            return {
                loaded: false,
                mapping,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/cohorts',
                    mapData: mapping.mapData,
                    unmapData: mapping.unmapData,
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['name'],
                        include: ['status_options', 'owners'],
                    },
                    callbacks: {
                        listed: this.listed
                    },
                }),
                owners: {},
                allStatusOptions: {},
                search: '',
                filters: {},
            }
        },

        computed: {

            breadcrumb() {
                return [
                    {
                        name: this.$t('reports.cohorts'),
                    },
                ]
            },

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            hasFilters() {
                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != 'name'
            }
        },

        created() {
            this.filtering.init(this)

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.owners = Array.isArray(resp.data.included.owners) ? {} : resp.data.included.owners
                this.allStatusOptions = resp.data.included.status_options
            })
        },

        mounted() {
            if (this.filters.name) {
                this.$refs.search.setFilter(this.filters.name.$text)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.name
                } else {
                    this.filters.name = {$text: val}
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        methods: {

            sortChange(data) {
                this.filtering.sortChange(data)
            },

            filterChange(data) {
                this.filtering.filterChange(data)
            },

            clearFilters() {
                this.ctrl.query.sort = ['name']
                this.filtering.clearFilters()
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            requestFilters() {
                let filters = {$and: []}
                
                // Owner.
                if (this.filters.owner) {
                    let inOwner = {owner_id: {$in: this.filters.owner.map(id => parseInt(id)).filter(val => val) }}
                    filters.$and.push(inOwner)
                }

                // Status.
                if (this.filters.status) {
                    let inStatus = {status: {$in: this.filters.status }}
                    filters.$and.push(inStatus)
                }

                // Search.
                if (this.filters.name) {
                    filters.$and.push({ name: this.filters.name})
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                
                return filters
            },

            reload() {
                this.ctrl.filter(this.requestFilters())
            },

            statusColor(status) {
                return {
                    draft: 'primary',
                    active: 'success',
                    archived: 'secondary',
                }[status]
            },

            statusLabel(status) {
                const key = {
                    draft: 'inactive',
                    active: 'active',
                    archived: 'archived',
                }[status]
                return this.$t('status.'+key)
            },

            handleCommand(cohortId, report) {
                this.$router.push({ name: 'report-cohort-' + report, params: { cohortId } })
            },
        }
    }
</script>
